import { confirm, prompt } from '@/lib/notifications';
import { formatAppointmentWorkflowStatusLabel } from '@/lib/work-items/appointmentRealtimeUtils';
import { formatAppointmentAdminOverrideStatusLabel } from '@/lib/work-items/appointmentAdminOverrideTransitions';

export const APPOINTMENT_ADMIN_OVERRIDE_REASON_MIN_LENGTH = 12;

const ADMIN_APPOINTMENT_TRAVEL_OVERRIDE_STATUSES = new Set([
  'on_route',
  'en_route',
  'in_progress',
  'checked_in',
  'working',
]);

export interface AppointmentAdminOverrideValidation {
  current_status: string | null;
  requested_status: string;
  blockers: string[];
  blocker_codes: string[];
  warnings: string[];
  requires_reason: boolean;
  requires_acknowledgement: boolean;
  requires_odometer: boolean;
  requires_travel_bypass_reason: boolean;
  requires_vehicle_inspection_acknowledgement: boolean;
  existing_odometer_reading: number | null;
  suggested_vehicle_id: string | null;
  suggested_vehicle_registration: string | null;
}

export interface PreparedAdminAppointmentOverrideRequest {
  requestBody: Record<string, unknown>;
  validation: AppointmentAdminOverrideValidation;
  successContext: AppointmentAdminOverrideSuccessContext;
}

export interface AppointmentAdminOverrideSuccessContext {
  subjectLabel: string;
  audienceSummaryLabel?: string | null;
  targetUserId?: number | null;
  targetUserIds?: number[];
}

export interface AppointmentAdminOverrideAssigneeOption {
  userId: number;
  displayName: string;
}

export interface AppointmentAdminOverrideAudienceSelection {
  targetUserId?: number | null;
  targetUserIds: number[];
  driverUserId?: number | null;
  driverLabel?: string | null;
  subjectLabel: string;
  audienceSummaryLabel?: string | null;
}

interface FetchValidationOptions {
  authToken: string;
  workItemId: string;
  requestedStatus: string;
  targetUserId?: number | null;
  targetUserIds?: number[];
  driverUserId?: number | null;
}

interface PrepareAdminAppointmentOverrideOptions extends FetchValidationOptions {
  currentStatus?: string | null;
  subjectLabel: string;
  audienceSummaryLabel?: string | null;
  title?: string | null;
  extraMessageLines?: string[];
  driverLabel?: string | null;
}

function getAudienceSummaryLabel(count: number, scope: 'single' | 'all', displayName?: string | null): string {
  if (scope === 'single') {
    return displayName ? `Only ${displayName}` : 'Only the selected field worker';
  }

  if (count === 2) {
    return 'Both assignees';
  }

  return `All ${count} assignees`;
}

function isAllAudienceSummaryLabel(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return value === 'Both assignees' || /^All\s+\d+\s+assignees$/i.test(value);
}

interface SuggestedVehicleTelemetry {
  liveOdometerKm: number | null;
  storedOdometerKm: number | null;
  trackingProvider: string | null;
  isTracked: boolean;
  registration: string | null;
}

function normalizeRequestedStatus(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function getAudienceSelectionTitle(requestedStatus: string): string {
  switch (normalizeRequestedStatus(requestedStatus)) {
    case 'on_route':
    case 'en_route':
      return 'Start travel for who?';
    case 'in_progress':
    case 'checked_in':
    case 'working':
      return 'Start work for who?';
    case 'paused':
    case 'on_hold':
      return 'Pause work for who?';
    default:
      return 'Apply override for who?';
  }
}

function getAudienceSelectionMessage(requestedStatus: string, assigneeCount: number): string {
  const normalizedStatus = normalizeRequestedStatus(requestedStatus);
  const assigneeLabel = assigneeCount === 2 ? 'both assignees' : `all ${assigneeCount} assignees`;

  switch (normalizedStatus) {
    case 'on_route':
    case 'en_route':
      return `Move ${assigneeLabel} to On Route together, or only start travel for one field worker?`;
    case 'in_progress':
    case 'checked_in':
    case 'working':
      return `Move ${assigneeLabel} into In Progress together, or only start work for one field worker?`;
    case 'paused':
    case 'on_hold':
      return `Pause ${assigneeLabel} together, or only pause work for one field worker?`;
    default:
      return `Apply this override to ${assigneeLabel}, or only one selected field worker?`;
  }
}

function getOverrideActionLabel(requestedStatus: string): string | null {
  const normalizedStatus = normalizeRequestedStatus(requestedStatus);

  switch (normalizedStatus) {
    case 'on_route':
    case 'en_route':
      return 'start travel';
    case 'in_progress':
    case 'checked_in':
    case 'working':
      return 'start work';
    case 'paused':
    case 'on_hold':
      return 'pause work';
    case 'resolved':
      return 'resolve the appointment';
    case 'partial':
      return 'mark the appointment partial';
    case 'unresolved':
      return 'mark the appointment unresolved';
    case 'rescheduled':
      return 'reschedule the appointment';
    case 'cancelled':
    case 'canceled':
      return 'cancel the appointment';
    default:
      return null;
  }
}

function getAppointmentOverrideConfirmAudienceLabel(options: {
  targetUserId?: number | null;
  targetUserIds?: number[];
}): string | null {
  const normalizedTargetUserIds = Array.isArray(options.targetUserIds)
    ? options.targetUserIds.filter((value): value is number => Number.isFinite(value) && value > 0)
    : [];

  if (normalizedTargetUserIds.length > 1) {
    return 'all assignees';
  }

  if (normalizedTargetUserIds.length === 1 || (typeof options.targetUserId === 'number' && Number.isFinite(options.targetUserId) && options.targetUserId > 0)) {
    return 'selected worker';
  }

  return null;
}

function lowercaseFirstLetter(value: string): string {
  return value.length > 0 ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;
}

function getAppointmentOverrideConfirmActionLabel(options: {
  requestedStatus: string;
  audienceLabel?: string | null;
}): string | null {
  const normalizedStatus = normalizeRequestedStatus(options.requestedStatus);
  const audienceLabel = options.audienceLabel || 'the selected field worker';

  switch (normalizedStatus) {
    case 'on_route':
    case 'en_route':
      return `Start travel for ${audienceLabel}`;
    case 'in_progress':
    case 'checked_in':
    case 'working':
      return `Start work for ${audienceLabel}`;
    case 'paused':
    case 'on_hold':
      return `Pause ${audienceLabel}`;
    case 'resolved':
      return `Resolve appointment for ${audienceLabel}`;
    case 'partial':
      return `Mark appointment partial for ${audienceLabel}`;
    case 'unresolved':
      return `Mark appointment unresolved for ${audienceLabel}`;
    case 'rescheduled':
      return `Reschedule appointment for ${audienceLabel}`;
    case 'cancelled':
    case 'canceled':
      return `Cancel appointment for ${audienceLabel}`;
    default:
      return null;
  }
}

function getAppointmentOverrideConfirmText(options: {
  requestedStatus: string;
  hasBlockers: boolean;
  targetUserId?: number | null;
  targetUserIds?: number[];
}): string {
  const audienceLabel = getAppointmentOverrideConfirmAudienceLabel(options);
  const actionLabel = getAppointmentOverrideConfirmActionLabel({
    requestedStatus: options.requestedStatus,
    audienceLabel,
  });
  if (!actionLabel) {
    return options.hasBlockers ? 'Override and continue' : 'Apply override';
  }

  return options.hasBlockers
    ? `Override blockers and ${lowercaseFirstLetter(actionLabel)}`
    : actionLabel;
}

export function formatAdminAppointmentOverrideSuccessMessage(options: {
  requestedStatus: string;
  successContext: AppointmentAdminOverrideSuccessContext;
}): string {
  const statusLabel = formatAppointmentAdminOverrideStatusLabel(options.requestedStatus);
  const normalizedTargetUserIds = Array.isArray(options.successContext.targetUserIds)
    ? options.successContext.targetUserIds.filter((value): value is number => Number.isFinite(value) && value > 0)
    : [];
  const isAllAssignees = normalizedTargetUserIds.length > 1 || isAllAudienceSummaryLabel(options.successContext.audienceSummaryLabel);

  if (isAllAssignees) {
    return `All assignees are now ${statusLabel}.`;
  }

  const subjectLabel = options.successContext.subjectLabel?.trim();
  if (subjectLabel) {
    return `Selected worker ${subjectLabel} is now ${statusLabel}.`;
  }

  return `The selected worker is now ${statusLabel}.`;
}

function getAppointmentOverrideConfirmationTitle(requestedStatus: string, hasBlockers: boolean): string {
  const actionLabel = getOverrideActionLabel(requestedStatus);

  if (!actionLabel) {
    return hasBlockers ? 'Override appointment blockers?' : 'Confirm appointment override';
  }

  if (hasBlockers) {
    return `Override blockers and ${actionLabel}?`;
  }

  return `Confirm ${actionLabel} override`;
}

async function promptForAssigneeSelection(options: {
  title: string;
  message: string;
  candidates: AppointmentAdminOverrideAssigneeOption[];
  placeholder: string;
}): Promise<AppointmentAdminOverrideAssigneeOption | null> {
  const { title, message, candidates, placeholder } = options;
  const numberedCandidates = candidates
    .map((candidate, index) => `${index + 1}. ${candidate.displayName}`)
    .join('\n');

  const result = await prompt({
    title,
    message: `${message}\n\n${numberedCandidates}`,
    placeholder,
    confirmText: 'Continue',
    cancelText: 'Cancel',
    required: true,
    validator: (value) => {
      const selection = Number(value.trim());
      if (!Number.isInteger(selection) || selection < 1 || selection > candidates.length) {
        return `Enter a number between 1 and ${candidates.length}.`;
      }
      return null;
    },
  });

  if (result == null) {
    return null;
  }

  const selectedIndex = Number(result.trim()) - 1;
  return candidates[selectedIndex] || null;
}

export async function resolveAppointmentAdminOverrideAudience(options: {
  requestedStatus: string;
  candidates: AppointmentAdminOverrideAssigneeOption[];
}): Promise<AppointmentAdminOverrideAudienceSelection | null> {
  const uniqueCandidates = Array.from(new Map(
    options.candidates
      .filter((candidate) => Number.isFinite(candidate.userId) && candidate.userId > 0)
      .map((candidate) => [candidate.userId, candidate]),
  ).values());

  if (uniqueCandidates.length === 0) {
    return {
      targetUserIds: [],
      subjectLabel: 'the assigned field worker',
      audienceSummaryLabel: null,
    };
  }

  const normalizedRequestedStatus = normalizeRequestedStatus(options.requestedStatus);
  const requiresDriverSelection = ADMIN_APPOINTMENT_TRAVEL_OVERRIDE_STATUSES.has(normalizedRequestedStatus);

  if (uniqueCandidates.length === 1) {
    const onlyCandidate = uniqueCandidates[0];
    return {
      targetUserId: onlyCandidate.userId,
      targetUserIds: [onlyCandidate.userId],
      driverUserId: requiresDriverSelection ? onlyCandidate.userId : null,
      driverLabel: requiresDriverSelection ? onlyCandidate.displayName : null,
      subjectLabel: onlyCandidate.displayName,
      audienceSummaryLabel: getAudienceSummaryLabel(uniqueCandidates.length, 'single', onlyCandidate.displayName),
    };
  }

  let scope: 'all' | 'single' | null = null;
  const confirmed = await confirm({
    title: getAudienceSelectionTitle(options.requestedStatus),
    message: getAudienceSelectionMessage(options.requestedStatus, uniqueCandidates.length),
    confirmText: uniqueCandidates.length === 2 ? 'All assignees' : `All ${uniqueCandidates.length} assignees`,
    cancelText: 'Cancel',
    secondaryConfirmText: 'Select 1 assignee',
    secondaryVariant: 'info',
    variant: 'info',
    onConfirm: () => {
      scope = 'all';
    },
    onSecondaryConfirm: () => {
      scope = 'single';
    },
  });

  if (!confirmed || !scope) {
    return null;
  }

  if (scope === 'single') {
    const selectedAssignee = await promptForAssigneeSelection({
      title: 'Which assignee should be updated?',
      message: 'Choose the field worker that should receive this admin override.',
      candidates: uniqueCandidates,
      placeholder: 'Type the assignee number',
    });

    if (!selectedAssignee) {
      return null;
    }

    return {
      targetUserId: selectedAssignee.userId,
      targetUserIds: [selectedAssignee.userId],
      driverUserId: requiresDriverSelection ? selectedAssignee.userId : null,
      driverLabel: requiresDriverSelection ? selectedAssignee.displayName : null,
      subjectLabel: selectedAssignee.displayName,
      audienceSummaryLabel: getAudienceSummaryLabel(uniqueCandidates.length, 'single', selectedAssignee.displayName),
    };
  }

  let driverSelection: AppointmentAdminOverrideAssigneeOption | null = null;
  if (requiresDriverSelection) {
    driverSelection = await promptForAssigneeSelection({
      title: 'Who is driving?',
      message: 'Choose the assignee whose vehicle and mileage should be used for this trip.',
      candidates: uniqueCandidates,
      placeholder: 'Type the driver number',
    });

    if (!driverSelection) {
      return null;
    }
  }

  return {
    targetUserIds: uniqueCandidates.map((candidate) => candidate.userId),
    driverUserId: driverSelection?.userId ?? null,
    driverLabel: driverSelection?.displayName ?? null,
    subjectLabel: `${uniqueCandidates.length} field workers`,
    audienceSummaryLabel: getAudienceSummaryLabel(uniqueCandidates.length, 'all'),
  };
}

async function fetchSuggestedVehicleTelemetry(options: {
  authToken: string;
  vehicleId: string;
}): Promise<SuggestedVehicleTelemetry | null> {
  const response = await fetch(`/api/v1/work-items/fleet?action=get_vehicle_info&vehicle_id=${encodeURIComponent(options.vehicleId)}`, {
    headers: {
      Authorization: `Bearer ${options.authToken}`,
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json().catch(() => null) as {
    success?: boolean;
    vehicle?: Record<string, unknown>;
  } | null;

  if (!payload?.success || !payload.vehicle) {
    return null;
  }

  const vehicle = payload.vehicle;
  const liveOdometerKm = Number(vehicle.live_odometer_km);
  const storedOdometerKm = Number(vehicle.stored_odometer_km);

  return {
    liveOdometerKm: Number.isFinite(liveOdometerKm) && liveOdometerKm >= 0 ? liveOdometerKm : null,
    storedOdometerKm: Number.isFinite(storedOdometerKm) && storedOdometerKm >= 0 ? storedOdometerKm : null,
    trackingProvider: typeof vehicle.tracking_provider === 'string' && vehicle.tracking_provider.trim()
      ? vehicle.tracking_provider.trim()
      : null,
    isTracked: Boolean(vehicle.is_tracked),
    registration: typeof vehicle.registration === 'string' && vehicle.registration.trim()
      ? vehicle.registration.trim()
      : null,
  };
}

function normalizeValidationPayload(value: unknown, requestedStatus: string): AppointmentAdminOverrideValidation {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};

  return {
    current_status: typeof record.current_status === 'string' ? record.current_status : null,
    requested_status: typeof record.requested_status === 'string' ? record.requested_status : requestedStatus,
    blockers: Array.isArray(record.blockers) ? record.blockers.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0) : [],
    blocker_codes: Array.isArray(record.blocker_codes) ? record.blocker_codes.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0) : [],
    warnings: Array.isArray(record.warnings) ? record.warnings.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0) : [],
    requires_reason: Boolean(record.requires_reason),
    requires_acknowledgement: Boolean(record.requires_acknowledgement),
    requires_odometer: Boolean(record.requires_odometer),
    requires_travel_bypass_reason: Boolean(record.requires_travel_bypass_reason),
    requires_vehicle_inspection_acknowledgement: Boolean(record.requires_vehicle_inspection_acknowledgement),
    existing_odometer_reading: Number.isFinite(Number(record.existing_odometer_reading)) ? Number(record.existing_odometer_reading) : null,
    suggested_vehicle_id: typeof record.suggested_vehicle_id === 'string' && record.suggested_vehicle_id.trim() ? record.suggested_vehicle_id : null,
    suggested_vehicle_registration: typeof record.suggested_vehicle_registration === 'string' && record.suggested_vehicle_registration.trim() ? record.suggested_vehicle_registration : null,
  };
}

export async function fetchAppointmentAdminOverrideValidation({
  authToken,
  workItemId,
  requestedStatus,
  targetUserId,
  targetUserIds,
  driverUserId,
}: FetchValidationOptions): Promise<AppointmentAdminOverrideValidation> {
  const response = await fetch('/api/v1/work-items/workflow-state', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      action: 'validate_admin_override_appointment_status',
      work_item_id: workItemId,
      requested_status: requestedStatus,
      metadata: {
        ...(typeof targetUserId === 'number' ? { target_user_id: targetUserId } : {}),
        ...(Array.isArray(targetUserIds) && targetUserIds.length > 0 ? { target_user_ids: targetUserIds } : {}),
        ...(typeof driverUserId === 'number' ? { driver_user_id: driverUserId } : {}),
      },
    }),
  });

  const payload = await response.json().catch(() => null) as { success?: boolean; error?: string; data?: { validation?: unknown } } | null;
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || 'Failed to validate appointment override');
  }

  return normalizeValidationPayload(payload.data?.validation, requestedStatus);
}

export async function prepareAdminAppointmentOverride({
  authToken,
  workItemId,
  requestedStatus,
  currentStatus,
  subjectLabel,
  audienceSummaryLabel,
  title,
  extraMessageLines = [],
  targetUserId,
  targetUserIds,
  driverUserId,
  driverLabel,
}: PrepareAdminAppointmentOverrideOptions): Promise<PreparedAdminAppointmentOverrideRequest | null> {
  const validation = await fetchAppointmentAdminOverrideValidation({
    authToken,
    workItemId,
    requestedStatus,
    targetUserId,
    targetUserIds,
    driverUserId,
  });

  let odometerReading: number | null = null;
  let mileageMessage: string | null = null;

  if (validation.requires_odometer) {
    let suggestedVehicleTelemetry: SuggestedVehicleTelemetry | null = null;

    if (validation.suggested_vehicle_id) {
      suggestedVehicleTelemetry = await fetchSuggestedVehicleTelemetry({
        authToken,
        vehicleId: validation.suggested_vehicle_id,
      }).catch(() => null);

      if (suggestedVehicleTelemetry?.liveOdometerKm != null) {
        odometerReading = suggestedVehicleTelemetry.liveOdometerKm;
        const providerLabel = suggestedVehicleTelemetry.trackingProvider || 'the live vehicle tracker';
        const vehicleLabel = suggestedVehicleTelemetry.registration || validation.suggested_vehicle_registration || 'the selected vehicle';
        mileageMessage = `Live mileage was fetched from ${providerLabel} for ${vehicleLabel}.`;
      }
    }

    if (odometerReading == null) {
    const captureLabel = validation.suggested_vehicle_registration
      ? `Enter the current odometer reading for ${validation.suggested_vehicle_registration}.`
      : driverLabel
        ? `Enter the current odometer reading for ${driverLabel}'s vehicle before overriding this appointment.`
        : 'Enter the current odometer reading before overriding this appointment.';
    const odometerInput = await prompt({
      title: 'Mileage required',
      message: suggestedVehicleTelemetry?.isTracked
        ? `${captureLabel}\n\nThe tracker did not return a live reading, so please capture the current mileage manually.`
        : captureLabel,
      placeholder: 'e.g. 125430',
      defaultValue: suggestedVehicleTelemetry?.storedOdometerKm != null
        ? String(suggestedVehicleTelemetry.storedOdometerKm)
        : validation.existing_odometer_reading != null
          ? String(validation.existing_odometer_reading)
          : '',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      required: true,
      validator: (value) => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) {
          return 'Please enter a valid odometer reading.';
        }
        return null;
      },
    });

    if (odometerInput == null) {
      return null;
    }

    odometerReading = Number(odometerInput);
    }
  }

  const resolvedCurrentStatus = validation.current_status || currentStatus || 'scheduled';
  const fromLabel = formatAppointmentWorkflowStatusLabel(resolvedCurrentStatus);
  const toLabel = formatAppointmentAdminOverrideStatusLabel(validation.requested_status || requestedStatus);

  const messageParts = [
    `Change ${subjectLabel} from ${fromLabel} to ${toLabel}? This will immediately override SmartTask and sync the workflow timers.`,
  ];

  if (audienceSummaryLabel) {
    messageParts.push('', `Selected scope: ${audienceSummaryLabel}.`);
  }

  if (validation.requires_travel_bypass_reason) {
    messageParts.push('', 'This change skips the normal travel step and starts work immediately. A reason is required.');
  }

  if (validation.requires_odometer) {
    messageParts.push('', 'Mileage will be captured as part of this override so travel / vehicle context stays aligned.');

    if (driverLabel) {
      messageParts.push(`Driver vehicle source: ${driverLabel}.`);
    }

    if (mileageMessage) {
      messageParts.push(mileageMessage);
    }
  }

  if (validation.blockers.length > 0) {
    messageParts.push(
      '',
      'The following blocker(s) are currently active and will be overridden if you continue:',
      ...validation.blockers.map((blocker) => `• ${blocker}`),
    );
  }

  if (validation.warnings.length > 0) {
    messageParts.push(
      '',
      'Please review:',
      ...validation.warnings.map((warning) => `• ${warning}`),
    );
  }

  if (extraMessageLines.length > 0) {
    messageParts.push('', ...extraMessageLines);
  }

  let overrideReason = '';
  const requiresReasonInput = validation.requires_reason || validation.requires_acknowledgement;

  console.log('[adminAppointmentOverrideClient] About to show confirm dialog:', {
    requiresReasonInput,
    requiresReason: validation.requires_reason,
    requiresAcknowledgement: validation.requires_acknowledgement,
    hasBlockers: validation.blockers.length > 0,
  });

  const confirmed = await confirm({
    title: title || getAppointmentOverrideConfirmationTitle(validation.requested_status || requestedStatus, validation.blockers.length > 0),
    message: messageParts.join('\n'),
    confirmText: getAppointmentOverrideConfirmText({
      requestedStatus: validation.requested_status || requestedStatus,
      hasBlockers: validation.blockers.length > 0,
      targetUserId,
      targetUserIds,
    }),
    cancelText: 'Cancel',
    variant: validation.blockers.length > 0 || validation.requires_travel_bypass_reason || ['resolved', 'partial', 'unresolved', 'rescheduled', 'cancelled'].includes(validation.requested_status)
      ? 'warning'
      : 'info',
    requireConfirmation: requiresReasonInput
      ? {
          type: 'textarea',
          label: validation.requires_travel_bypass_reason
            ? 'Reason for bypassing travel'
            : 'Override reason',
          placeholder: validation.requires_travel_bypass_reason
            ? 'Explain why travel was skipped and work is starting directly…'
            : 'Explain why this override is being applied…',
          minLength: APPOINTMENT_ADMIN_OVERRIDE_REASON_MIN_LENGTH,
        }
      : undefined,
    onConfirm: (result) => {
      overrideReason = typeof result?.inputValue === 'string' ? result.inputValue.trim() : '';
      console.log('[adminAppointmentOverrideClient] Confirm dialog onConfirm called with:', {
        result,
        overrideReason,
        overrideReasonLength: overrideReason.length,
      });
    },
  });

  if (!confirmed) {
    return null;
  }

  console.log('[adminAppointmentOverrideClient] Preparing override request with:', {
    overrideReason,
    overrideReasonLength: overrideReason.length,
    requiresReason: validation.requires_reason,
    requiresAcknowledgement: validation.requires_acknowledgement,
    validation: validation,
  });

  return {
    validation,
    successContext: {
		  subjectLabel,
		  audienceSummaryLabel,
		  targetUserId,
		  targetUserIds,
	    },
    requestBody: {
      action: 'admin_override_appointment_status',
      work_item_id: workItemId,
      requested_status: requestedStatus,
      ...(odometerReading != null ? { odometer_reading: odometerReading } : {}),
      ...(validation.suggested_vehicle_id ? { vehicle_id: validation.suggested_vehicle_id } : {}),
      ...(validation.suggested_vehicle_registration ? { vehicle_registration: validation.suggested_vehicle_registration } : {}),
      metadata: {
        ...(typeof targetUserId === 'number' ? { target_user_id: targetUserId } : {}),
        ...(Array.isArray(targetUserIds) && targetUserIds.length > 0 ? { target_user_ids: targetUserIds } : {}),
        ...(typeof driverUserId === 'number' ? { driver_user_id: driverUserId } : {}),
        ...(overrideReason ? {
          override_reason: overrideReason,
          ...(validation.requires_travel_bypass_reason ? { travel_bypass_reason: overrideReason } : {}),
        } : {}),
        ...(validation.requires_acknowledgement || validation.requires_reason ? { override_acknowledged: true } : {}),
      },
    },
  };
}
